import { InspectDatabase } from './database.structure';
import { MineLocationModel } from './types';
import { MineLocationViewModel } from './viewmodels.types';

export default class DatabaseMineLocationMethods {
  private db: InspectDatabase;

  constructor(db: InspectDatabase) {
    this.db = db;
  }

  public async getMineLocations(): Promise<MineLocationModel[]> {
    return await this.db.mineLocations.toArray();
  }

  /** Scoped variant for list screens: joins only the requested mine locations. The
   * fleet-wide variant runs ~3 queries per mine location (~3,000 round-trips) -
   * the Home screen only needs the handful referenced by its listed inspections. */
  public async getMineLocationsWithDataByIds(
    ids: number[],
  ): Promise<MineLocationViewModel[]> {
    const unique = [...new Set(ids)].filter(
      (id): id is number => typeof id === 'number' && id > 0,
    );
    if (unique.length === 0) {
      return [];
    }
    const mineLocations = await this.db.mineLocations
      .where('id')
      .anyOf(unique)
      .toArray();
    return this.joinMineLocationData(mineLocations);
  }

  /**
   * Whether the site has at least one mine location (laydown yard) in the local DB (#10363).
   * Local data is treated as correct until the next sync.
   */
  public async siteHasLaydownYard(siteId: number): Promise<boolean> {
    if (siteId <= 0) {
      return false;
    }
    return (
      (await this.db.mineLocations.where('siteId').equals(siteId).count()) > 0
    );
  }

  public async getMineLocationsWithData(): Promise<MineLocationViewModel[]> {
    const mineLocations = await this.db.mineLocations.toArray();
    return this.joinMineLocationData(mineLocations);
  }

  private async joinMineLocationData(
    mineLocations: MineLocationModel[],
  ): Promise<MineLocationViewModel[]> {
    // Return mine locations with site and customer data
    const data = await Promise.all(
      mineLocations.map(async (mineLocation) => {
        const site = await this.db.sites.get(mineLocation.siteId);
        if (!site) {
          return null;
        }
        const customer = await this.db.customers.get(site.customerId);

        const equipments = await this.db.equipments
          .where('mineLocationId')
          .equals(mineLocation.id)
          .toArray();

        return {
          mineLocation,
          customerId: customer?.id,
          customerName: customer?.name,
          siteId: site.id,
          siteName: site.name,
          equipments: equipments,
        } as MineLocationViewModel;
      }),
    );

    return data.filter((item) => item !== null) as MineLocationViewModel[];
  }
}
